import { FormControlStatus } from '@angular/forms';
import { ReportFormRuleTemplate, RuleContext } from '@navstation-portal/reporting/data-model/ui';
import {
  FormState,
  ReportTabRulesKey,
  isReportTabRulesKey,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { resolveValue } from '@navstation-portal/reporting/util/form-rules';
import { isFieldOnScreen } from './form-rule-lookup.utility';
import { isEmptyValue, isOutsideBounds } from './form-validity.utility';
import { isStateField } from './vm-builder-helpers';

/** One field of one tab, and the status the store writes for it. */
export interface BoundFieldStatus {
  tabId: ReportTabRulesKey;
  sectionId: string;
  fieldId: string;
  status: FormControlStatus;
}

/**
 * Finds each field whose value is outside the min or the max of the template,
 * and gives the status the store writes for it.
 *
 * A control checks its bound only after its tab renders. Before that, init
 * stamps each status from the form-field-rules payload, which holds no bound.
 * So a value outside its bound on a tab that the user did not open would not
 * block the submit. Each tab stays rendered once the user opens it, but a
 * report that loads again starts with no tab rendered.
 *
 * - A value outside its bound gets INVALID, and goes in `marked`.
 * - A value in `marked` from the last run, that is now inside its bound, gets
 *   VALID back. The store clears no INVALID that it did not write, because a
 *   control or init can hold an INVALID for another reason.
 *
 * A field that the template hides is left alone. findStaleInvalidFields gives
 * it DISABLED. An array-of-rows section is left alone too, as there. Its rows
 * are checked when their tab renders.
 *
 * Pass `marked` from the last run, and keep the `marked` that it returns for the
 * next run.
 */
export const findBoundStatuses = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  formStateByTab: Partial<Record<ReportTabRulesKey, FormState>>,
  markedLastRun: ReadonlySet<string> = new Set()
): { statuses: BoundFieldStatus[]; marked: Set<string> } => {
  const statuses: BoundFieldStatus[] = [];
  const marked = new Set<string>();

  for (const [tabId, tab] of Object.entries(template?.tabs ?? {})) {
    if (!isReportTabRulesKey(tabId)) continue;
    const formState = formStateByTab[tabId];
    if (!formState) continue;

    for (const [sectionId, section] of Object.entries(tab.sections ?? {})) {
      for (const [fieldId, field] of Object.entries(section.fields ?? {})) {
        if (!field.min && !field.max) continue;
        const entry = formState[sectionId]?.[fieldId];
        if (!isStateField(entry)) continue;
        if (!isFieldOnScreen(template, context, tabId, sectionId, fieldId)) continue;

        const key = `${tabId}.${sectionId}.${fieldId}`;
        const min = resolveValue(field.min, context);
        const max = resolveValue(field.max, context);

        if (isOutsideBounds(entry.value, min, max)) {
          marked.add(key);
          if (entry.status !== 'INVALID') statuses.push({ tabId, sectionId, fieldId, status: 'INVALID' });
          continue;
        }

        // An empty field that holds INVALID is a required field. Init or its
        // control gave it that status.
        if (markedLastRun.has(key) && entry.status === 'INVALID' && !isEmptyValue(entry.value)) {
          statuses.push({ tabId, sectionId, fieldId, status: 'VALID' });
        }
      }
    }
  }

  return { statuses, marked };
};
